// src/components/CRNAProfileForm/CRNAProfileForm.js
import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import {
  Container, Form, Button,
  Alert, Row, Col, ProgressBar, Spinner
} from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import geolocator from 'geolocator';
// Steps
import WorkPreferencesSection      from '../ConsultationForm/WorkPreferencesSection';
import SchedulePreferencesSection  from './SchedulePreferencesSection';
import ClinicalProficiencySection  from './ClinicalProficiencySection';
import CareSettingsSection         from './CareSettingsSection';
import CertificationSection        from './CertificationSection';

import './CRNAProfileForm.css';

const BACKEND_URL = process.env.REACT_APP_RXROOSTER_BACKEND_URL;
const totalSteps  = 5;

geolocator.config({
  language: 'en',
  // we’re not using Google here, just want coordinates + IP fallback
  google: { version: null, key: null },
  // timeouts in ms
  timeout:     8000,    // wait up to 8s for a GPS fix
  maximumWait: 10000,   // overall 10s before giving up
  maximumAge:  600_000, // accept up to 10-minute-old cached loc
});

const CRNAProfileForm = ({ onComplete }) => {
  const { auth } = useContext(AuthContext);

  // formData is null until we fetch
  const [formData, setFormData]  = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [errorMessage, setErrorMessage] = useState('');
  const [status, setStatus] = useState({
    submitted: false,
    submitting: false,
    info: { error: false, msg: null }
  });

  // 1) Fetch existing profile on mount
  useEffect(() => {
    (async () => {
      if (!auth?.token) return;
      try {
        const res = await fetch(
          `${BACKEND_URL}/provider/${auth.user.sub}/profile`,
          { headers: { Authorization: `Bearer ${auth.token}` } }
        );
        const { data } = await res.json();
        // Map backend shape into your formData shape:
        const p = data || {};
        setFormData({
          // Step 1
          locationPreference:    p.locationPreference    || '',
          radiusPreference:      p.radiusPreference      ?? '50',
          yearsOfExperience:     p.yearsOfExperience     || '',
          desiredWorkType:       p.desiredWorkType       || [],
          availability: {
            contractType:        p.availability?.contractType     || [],
            desiredSchedule:     p.availability?.desiredSchedule  || [],
            availabilityDate:    p.availability?.availabilityDate || ''
          },
          compensation: {
            type:   p.compensation?.type   || '',
            amount: p.compensation?.amount || ''
          },

          // Step 3
          areasOfProficiency:     p.areasOfProficiency    || [],
          emrExperience:          p.emrExperience         || [],

          // Step 4
          facilityPreferences: {
            careTeamModel:     p.facilityPreferences?.careTeamModel   || '',
            preferredSettings: p.facilityPreferences?.preferredSettings || []
          },

          // Step 5
          professionalCertifications: p.professionalCertifications || [],
          activeCertifications:       p.activeCertifications       || [],
          crnaCertificationStatus:    p.crnaCertificationStatus    || '',

          // For inputs: join arrays back into comma‑strings
          stateLicensesInput:      (p.stateLicenses      || []).join(', '),
          stateAPRNLicensesInput:  (p.stateAPRNLicenses  || []).join(', ')
        });
      } catch (err) {
        console.error('Failed to load profile for edit', err);
        setFormData({}); // show form anyway, empty
      }
    })();
  }, [auth]);

  // Show spinner until formData is ready
  if (formData === null) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" />
      </div>
    );
  }


    async function detectLocation() {
    return new Promise((resolve, reject) => {
      const opts = {
        enableHighAccuracy: true,
        desiredAccuracy:    30,
        fallbackToIP:       true,
        addressLookup:      false,
        timezone:           false,
        timeout:            8000,
        maximumWait:        10000,
        maximumAge:         600000,
      };
      geolocator.locate(opts, (err, location) => {
        if (err) {
          return reject(err);
        }
        const { latitude, longitude } = location.coords || {};
        if(latitude && longitude) {
          resolve({
            type: 'Point',
            coordinates: [longitude, latitude]
          });
        }
        resolve({
          type: 'Point',
        })
      });
    });
  }


  // 2) Utility to PUT updates
  // const updateProfile = async () => {
  //   try {
  //     setStatus(s => ({ ...s, submitting: true }));
  //     let geoLocation = null;
  //     try {
  //       geoLocation = await detectLocation();
  //     } catch (e) {
  //       console.warn('Could not detect location, falling back to null:', e);
  //     }
  //     const res = await fetch(
  //       `${BACKEND_URL}/provider/${auth.user.sub}/profile`,
  //       {
  //         method: 'PUT',
  //         headers: {
  //           'Content-Type': 'application/json',
  //           Authorization: `Bearer ${auth.token}`
  //         },
  //         body: JSON.stringify({
  //           ...formData,
  //           // convert back to arrays
  //           stateLicenses:     formData.stateLicensesInput
  //                                 .split(',').map(s => s.trim()).filter(Boolean),
  //           stateAPRNLicenses: formData.stateAPRNLicensesInput
  //                                 .split(',').map(s => s.trim()).filter(Boolean),
  //           ...(geoLocation ? { geoLocation } : {})
  //         })
  //       }
  //     );
  //     if (!res.ok) throw new Error();
  //     setStatus({ submitted: true, submitting: false, info: { error: false, msg: 'Saved successfully.' } });
  //   } catch (err) {
  //     console.error(err);
  //     setStatus({
  //       submitted: true,
  //       submitting: false,
  //       info: { error: true, msg: 'Failed to save. Please try again.' }
  //     });
  //   }
  // };
   const updateProfile = async () => {
    setStatus(s => ({ ...s, submitting: true }));

    // 1) Kick off location detection, but don’t await it
    const geoPromise = detectLocation().catch(err => {
      console.warn('Geo detect failed, skipping:', err);
      return null;
    });

    // 2) Immediately send the profile data (without waiting on geo)
    const payload = {
      ...formData,
      stateLicenses: formData.stateLicensesInput
        .split(',').map(s=>s.trim()).filter(Boolean),
      stateAPRNLicenses: formData.stateAPRNLicensesInput
        .split(',').map(s=>s.trim()).filter(Boolean),
    };

    let res;
    try {
      res = await fetch(
        `${BACKEND_URL}/provider/${auth.user.sub}/profile`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth.token}`
          },
          body: JSON.stringify(payload)
        }
      );
      if (!res.ok) throw new Error('Save failed');
      setStatus({ submitted: true, submitting: false, info: { error: false, msg: 'Saved successfully.' } });
    } catch (err) {
      console.error(err);
      setStatus({ submitted: true, submitting: false, info: { error: true, msg: 'Failed to save. Please try again.' } });
      return;
    }

    // 3) Once geo finishes, send a quick PATCH to update just the geoLocation
    geoPromise.then(async geoLocation => {
      if (!geoLocation) return;
      try {
        await fetch(
          `${BACKEND_URL}/provider/${auth.user.sub}/profile`,
          {
            method: 'PATCH',            // use PATCH to partially update
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${auth.token}`
            },
            body: JSON.stringify({ geoLocation })
          }
        );
      } catch (e) {
        console.warn('Geo update failed:', e);
      }
    });
  };

  // 3) Validation logic per step (unchanged)
  const validateStep = () => {
    let valid = true;
    let missingFields = [];
    
    if (currentStep === 1) {
      if (!formData.locationPreference.trim()) missingFields.push('Preferred Location');
      if (!formData.yearsOfExperience) missingFields.push('Years of Experience');
      if (!formData.desiredWorkType) missingFields.push('Desired Type of Work');
      if (!formData.compensation.type) missingFields.push('Compensation Type');
      if (!formData.compensation.amount) missingFields.push('Compensation Amount');
      if ((formData.availability.contractType || []).length === 0) missingFields.push('Desired Contract Type');
      if (missingFields.length) {
        valid = false;
        setErrorMessage('Please complete: ' + missingFields.join(', ') + '.');
      }
    } else if (currentStep === 2) {
      if (!formData.availability.desiredSchedule) missingFields.push('Desired Schedule');
      if (!formData.availability.availabilityDate) missingFields.push('Availability Date');
      if (missingFields.length) {
        valid = false;
        setErrorMessage('Please complete: ' + missingFields.join(', ') + '.');
      }
    } else if (currentStep === 4) {
      if (!formData.facilityPreferences.careTeamModel) missingFields.push('Care Team Model');
      if ((formData.facilityPreferences.preferredSettings || []).length === 0) missingFields.push('Preferred Settings');
      if (missingFields.length) {
        valid = false;
        setErrorMessage('Please complete: ' + missingFields.join(', ') + '.');
      }
    } else if (currentStep === 5) {
      
      if (!formData.stateLicensesInput.trim()) missingFields.push('State RN Licenses');
      if ((formData.activeCertifications || []).length === 0) missingFields.push('Active Certifications');
      if (!formData.crnaCertificationStatus) missingFields.push('CRNA Certification Status');
      if (missingFields.length) {
        valid = false;
        setErrorMessage('Please complete: ' + missingFields.join(', ') + '.');
      }
      formData.stateLicenses = formData.stateLicensesInput.split(',');
      formData.stateAPRNLicenses = formData.stateAPRNLicensesInput.split(',');
    }
    return valid;
  };

  // 4) Navigation
  const handleNext = () => {
    if (!validateStep()) return;
    updateProfile();
    setErrorMessage('');
    setCurrentStep(s => s + 1);
  };
  const handleBack = () => {
    setErrorMessage('');
    setCurrentStep(s => s - 1);
  };
  const handleFinalSubmission = () => {
    if (!validateStep()) return;
    updateProfile().then(() => {
      if (onComplete) onComplete();
    });
  };

  // 5) Handle every field change
  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(f => ({
        ...f,
        [parent]: {
          ...f[parent],
          [child]:
            type === 'checkbox'
              ? checked
                ? [...(f[parent][child]||[]), value]
                : (f[parent][child]||[]).filter(x => x !== value)
              : value
        }
      }));
    } else {
      if (type === 'checkbox') {
        setFormData(f => ({
          ...f,
          [name]: checked
            ? [...(f[name]||[]), value]
            : (f[name]||[]).filter(x => x !== value)
        }));
      } else {
        setFormData(f => ({ ...f, [name]: value }));
      }
    }
  };

  // 6) Stepper animation settings
  const stepVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit:    { opacity: 0, y: -20 }
  };

  // 7) Swap in the right section
  const renderStep = () => {
    switch (currentStep) {
      case 1: return <WorkPreferencesSection       formData={formData} onChange={handleChange} />;
      case 2: return <SchedulePreferencesSection   formData={formData} onChange={handleChange} />;
      case 3: return <ClinicalProficiencySection   formData={formData} onChange={handleChange} />;
      case 4: return <CareSettingsSection          formData={formData} onChange={handleChange} />;
      case 5: return <CertificationSection         formData={formData} onChange={handleChange} />;
      default: return null;
    }
  };

  return (
    <Container fluid className="my-5 crna-profile-container">
      <Row className="justify-content-center">
        <Col xs={12} md={10} lg={8}>
          <ProgressBar
            now={(currentStep/totalSteps)*100}
            label={`Step ${currentStep} of ${totalSteps}`}
            className="mb-4"
            animated
            striped
          />
          <h2 className="text-center mb-4">Edit Your CRNA Profile</h2>

          <AnimatePresence exitBeforeEnter>
            <motion.div
              key={currentStep}
              variants={stepVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.3 }}
            >
              {errorMessage &&
                <Alert variant="danger" className="text-center">
                  {errorMessage}
                </Alert>
              }

              <Form onSubmit={e=>e.preventDefault()}>
                {renderStep()}

                <div className="d-flex justify-content-between mt-4">
                  {currentStep > 1
                    ? <Button onClick={handleBack} variant="secondary">Back</Button>
                    : <div />
                  }
                  {currentStep < totalSteps
                    ? <Button onClick={handleNext} variant="primary">Next</Button>
                    : <Button
                        onClick={handleFinalSubmission}
                        variant="primary"
                        disabled={status.submitting}
                      >
                        {status.submitting ? 'Saving…' : 'Save & Close'}
                      </Button>
                  }
                </div>
              </Form>
            </motion.div>
          </AnimatePresence>

          {status.submitted && status.info.msg && (
            <Alert
              variant={status.info.error ? 'danger' : 'success'}
              className="mt-4 text-center"
              dismissible
              onClose={()=>setStatus(s=>({ ...s, info:{ error:false, msg:null } }))}
            >
              {status.info.msg}
            </Alert>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default CRNAProfileForm;
